<template>
  <file-pond name="camera" ref="pond" accepted-file-types="image/png, image/jpeg, image/gif" credits="false"
    :label-idle="labelIdle" allow-multiple="true" allow-drop="false" allow-browse="true" :capture-method="null"
    required="false" imagePreviewHeight="192" imagePreviewMaxInstantPreviewFileSize="225000" :files="files"
    @init="handleFilePondInit" :beforeAddFile="handleBeforeAddFile" :beforeRemoveFile="handleBeforeRemoveFile" />
</template>

<script>
import i18n from '../i18n/i18n';
import store from '../store/store';

import md5 from 'crypto-js/md5';
import skaler from 'skaler';
import Compressor from 'compressorjs';

import vueFilePond from 'vue-filepond';
import ptBr from 'filepond/locale/pt-br';
import 'filepond/dist/filepond.min.css';
import '../assets/third_party/filepond_overrides.css';

import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';

import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css';

const FilePond = vueFilePond(
  FilePondPluginFileValidateType,
  FilePondPluginImagePreview
);

export default {
  name: 'Camera',
  components: {
    FilePond,
  },
  props: {
    idleText: String,
  },
  data() {
    return {
      i18n: i18n,
      files: null,
      optimizationPending: [],
      optimizedFiles: {},
    };
  },
  methods: {
    handleFilePondInit() { },
    handleBeforeAddFile(pondFile) {
      const fileReader = new FileReader();
      fileReader.onload = (e) => {
        const imgMd5 = md5(e.target.result).toString();

        this.startImgOptimization(imgMd5, pondFile.file);
      };
      fileReader.readAsBinaryString(pondFile.file);

      return true;
    },
    handleBeforeRemoveFile(pondFile) {
      const fileReader = new FileReader();
      fileReader.onload = (e) => {
        const imgMd5 = md5(e.target.result).toString();

        this.waitForOptimizedFiles((_optimizedFiles) => {
          // Creates a new obj without the removed img.
          const refreshedOptimizedFiles = Object.assign({}, this.optimizedFiles);
          delete refreshedOptimizedFiles[imgMd5];
          this.optimizedFiles = refreshedOptimizedFiles;
          this.optimizationPending = this.cloneOptimizationQueueWithoutOptimizationCompletedImg(imgMd5);
          store.commit('deactivateFullscreenOverlay');
        });
      };
      store.commit('activateFullscreenOverlay');
      fileReader.readAsBinaryString(pondFile.file);

      return true;
    },
    startImgOptimization(imgMd5, file) {
      this.optimizationPending.push(imgMd5);
      console.log(`INFO: Pushed ${imgMd5} to the optimization queue.`);

      const img = new Image();
      img.onload = () => {
        img.width >= img.height
          ? this.performImgOptimization(imgMd5, file, { width: 1000 })
          : this.performImgOptimization(imgMd5, file, { width: 563 });
      };

      const fileReader = new FileReader();
      fileReader.onload = (e) => {
        img.src = e.target.result;
      };
      fileReader.readAsDataURL(file);
    },
    async performImgOptimization(originalImgMd5, file, optimizationOpts) {
      const resized = await skaler(file, optimizationOpts);

      new Compressor(resized, {
        checkOrientation: false,
        maxWidth: 4096,
        maxHeight: 4096,
        quality: 0.7,
        convertSize: 3000000,
        success: (compressed) => {
          console.log(`INFO: ${originalImgMd5} optimization completed.`);
          const newOptimizedImg = {};
          newOptimizedImg[originalImgMd5] = compressed;
          this.optimizedFiles = { ...(this.optimizedFiles), ...newOptimizedImg };
          this.optimizationPending = this.cloneOptimizationQueueWithoutOptimizationCompletedImg(originalImgMd5);
        },
        error: (_err) => {
          // Since the compression failed, we have to be satisfied by just using the resized img.
          console.log(`INFO: ${originalImgMd5} optimization completed (with non-fatal errors).`);
          const newOptimizedImg = {};
          newOptimizedImg[originalImgMd5] = resized;
          this.optimizedFiles = { ...(this.optimizedFiles), ...newOptimizedImg };
          this.optimizationPending = this.cloneOptimizationQueueWithoutOptimizationCompletedImg(originalImgMd5);
        }
      });
    },
    cloneOptimizationQueueWithoutOptimizationCompletedImg(optimizedImgMd5) {
      console.log(`INFO: ${optimizedImgMd5} removed from optimization queue (if present).`);
      return this.optimizationPending.filter((imgMd5) => imgMd5 !== optimizedImgMd5);
    },
    anyFiles() {
      return this.$refs.pond.getFiles().length > 0;
    },
    anyOptimizationPending() {
      return Object.keys(this.optimizationPending).length > 0;
    },
    getFileBlobs() {
      return this.$refs.pond.getFiles().map((pondFile) => pondFile.file);
    },
    waitForOptimizedFiles(cb) {
      if (this.anyOptimizationPending()) {
        setTimeout(() => (this.waitForOptimizedFiles(cb)), 250);
      } else {
        cb(Object.values(this.optimizedFiles));
      }
    },
  },
  computed: {
    labelIdle() {
      if (this.idleText)
        return `<span class="filepond--label-action">${this.idleText}</span>`;
      else return this.i18n.translate('thirdParty.filepond.labelIdle');
    },
  },
};
</script>
